import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import DocumentUploader from '@/components/DocumentUploader';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  UserCheck, Plus, Mail, Calendar, Award, Pencil, Trash2, Shield,
  Download, FileText, ClipboardCheck, CheckCircle, XCircle, Clock, AlertTriangle, GraduationCap, Upload, Star, Phone, User, MapPin, Ban, Archive, RotateCcw, Users, Search, Eye, Printer, Edit, Send, Camera, Save
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import StaffQualifications from '@/components/StaffQualifications';
import StaffPerformance from '@/components/StaffPerformance';
import ContinuingEducationForm from '@/components/ContinuingEducationForm';
import StaffRecordForm from '@/components/StaffRecordForm';
import OrientationChecklistForm from '@/components/OrientationChecklistForm';
import TransportationTrainingForm from '@/components/TransportationTrainingForm';
import FormComplianceDashboard from '@/components/FormComplianceDashboard';
import { toast } from 'sonner';

import API from '@/config/api';
// API imported from config;

const POSITIONS = [
  { value: 'Center Director', label: 'Center Director' },
  { value: 'Lead Teacher', label: 'Lead Teacher' },
  { value: 'Assistant Teacher', label: 'Assistant Teacher' },
  { value: 'Assistant Child Care Teacher', label: 'Assistant Child Care Teacher' },
  { value: 'Program Aide', label: 'Program Aide' },
  { value: 'Meal Preparation Personnel', label: 'Meal Preparation Personnel' },
  { value: 'Driver', label: 'Driver' },
  { value: 'Volunteer', label: 'Volunteer' },
  { value: 'Aide', label: 'Aide' },
  { value: 'Director', label: 'Director' },
  { value: 'Administrator', label: 'Administrator' },
  { value: 'Float Teacher', label: 'Float Teacher' },
];

const STATUS_CODES = {
  'X': { label: 'Met', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  'O': { label: 'Not Met', color: 'bg-red-100 text-red-700', icon: XCircle },
  'ND': { label: 'Not Yet Due', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  'NA': { label: 'N/A', color: 'bg-gray-100 text-gray-600', icon: AlertTriangle },
};

// A date input that only saves on blur AND validates year is between 1900-2100.
// Prevents bogus saves (e.g., "0002-05-10") that happen when HTML date inputs
// fire onChange mid-typing of the year.
const ComplianceDateInput = ({ value, onSave, className = 'w-36 h-8 text-xs', testId, disabled = false }) => {
  const [localVal, setLocalVal] = React.useState(value || '');
  React.useEffect(() => { setLocalVal(value || ''); }, [value]);

  const commit = async (val) => {
    if (val && val.length >= 10) {
      const year = parseInt(val.slice(0, 4), 10);
      if (isNaN(year) || year < 1900 || year > 2100) {
        toast.error('Invalid year — please enter a 4-digit year between 1900 and 2100');
        setLocalVal(value || '');
        return;
      }
    }
    if (val !== (value || '')) {
      await onSave(val);
    }
  };

  return (
    <Input
      type="date"
      value={localVal}
      min="1900-01-01"
      max="2100-12-31"
      disabled={disabled}
      onChange={(e) => setLocalVal(e.target.value)}
      onBlur={(e) => commit(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
      className={className}
      data-testid={testId}
    />
  );
};

const StaffPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [archivedStaff, setArchivedStaff] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [shiftFilter, setShiftFilter] = useState('all');
  const [shiftTimes, setShiftTimes] = useState([]);
  const [activeTab, setActiveTab] = useState('active');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [staffToDelete, setStaffToDelete] = useState(null);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [staffToRestore, setStaffToRestore] = useState(null);
  const [permanentDeleteDialogOpen, setPermanentDeleteDialogOpen] = useState(false);
  const [staffToPermanentDelete, setStaffToPermanentDelete] = useState(null);
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [staffToSuspend, setStaffToSuspend] = useState(null);
  const [suspendLoading, setSuspendLoading] = useState(false);
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [selectedStaffForPermissions, setSelectedStaffForPermissions] = useState(null);
  const [dcfDialogOpen, setDcfDialogOpen] = useState(false);
  const [selectedStaffForDCF, setSelectedStaffForDCF] = useState(null);
  const [dcfChecklistItems, setDcfChecklistItems] = useState([]);
  const [dcfOrientationItems, setDcfOrientationItems] = useState([]);
  const [dcfChecklist, setDcfChecklist] = useState({});
  const [orientationStatus, setOrientationStatus] = useState({});
  const [orientationDocs, setOrientationDocs] = useState({});
  const [uploadingOrientation, setUploadingOrientation] = useState(null);
  const [dateRangeStart, setDateRangeStart] = useState('');
  const [dateRangeEnd, setDateRangeEnd] = useState('');
  
  // Fillable forms state
  const [continuingEducationFormOpen, setContinuingEducationFormOpen] = useState(false);
  const [staffRecordFormOpen, setStaffRecordFormOpen] = useState(false);
  const [orientationChecklistFormOpen, setOrientationChecklistFormOpen] = useState(false);
  const [transportTrainingOpen, setTransportTrainingOpen] = useState(false);
  const [uploadingFormDoc, setUploadingFormDoc] = useState(null);
  const formFileInputRef = React.useRef(null);
  const [pendingUploadDocType, setPendingUploadDocType] = useState(null);
  const [uploadedFormDocs, setUploadedFormDocs] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [editingStaffName, setEditingStaffName] = useState(false);
  const [editStaffName, setEditStaffName] = useState('');
  
  // Invite new staff state
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteData, setInviteData] = useState({
    name: '',
    email: '',
    position: '',
    phone: '',
    hire_date: ''
  });
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  
  // Staff profile view dialog
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [selectedStaffProfile, setSelectedStaffProfile] = useState(null);
  
  const [permissions, setPermissions] = useState({
    // Children Management
    can_add_edit_children: true,
    can_view_children: true,
    // Waitlist
    can_manage_waitlist: false,
    // Staff
    can_view_staff_profiles: false,
    // Schedules
    can_add_edit_schedules: false,
    can_view_schedules: true,
    // Other Features
    can_use_ratio_calculator: true,
    can_manage_attendance: true,
    can_view_compliance: true,
    can_edit_compliance: false,
    can_view_billing: false,
    can_create_invoices: false,
    // Messaging
    can_message_parents_and_staff: true,
    can_message_staff_only: false,
    can_create_reports: true
  });
  const [formData, setFormData] = useState({
    user_id: '',
    position: '',
    usual_shift: 'Full Day',
    certifications: '',
    hire_date: '',
    training_completed: ''
  });

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchData();
    fetchDCFItems();
  }, []);

  const formatTime = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':');
    const hr = parseInt(h);
    const ampm = hr >= 12 ? 'PM' : 'AM';
    const hr12 = hr === 0 ? 12 : hr > 12 ? hr - 12 : hr;
    return `${hr12}:${m} ${ampm}`;
  };

  const fetchData = async () => {
    try {
      const [staffRes, usersRes, archivedRes, shiftTimesRes] = await Promise.all([
        axios.get(`${API}/staff`, getAuthHeaders()),
        axios.get(`${API}/users?role=provider`, getAuthHeaders()),
        axios.get(`${API}/staff/archived`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/settings/shift-times`, getAuthHeaders()).catch(() => ({ data: [] }))
      ]);
      setStaff(staffRes.data);
      setArchivedStaff(archivedRes.data);
      setShiftTimes(shiftTimesRes.data || []);
      const staffUserIds = staffRes.data.map(s => s.user_id);
      const availableUsers = usersRes.data.filter(u => !staffUserIds.includes(u.id));
      setUsers(availableUsers);
    } catch (error) {
      toast.error('Failed to load staff data');
    } finally {
      setLoading(false);
    }
  };

  const fetchDCFItems = async () => {
    try {
      const res = await axios.get(`${API}/staff/dcf-checklist-items`, getAuthHeaders());
      setDcfChecklistItems(res.data.checklist_items || []);
      setDcfOrientationItems(res.data.orientation_items || []);
    } catch (error) {
      console.error('Failed to fetch DCF items');
    }
  };

  const resetForm = () => {
    setFormData({
      user_id: '',
      position: '',
      usual_shift: 'Full Day',
      certifications: '',
      hire_date: '',
      training_completed: ''
    });
    setEditingStaff(null);
  };

  const handleOpenDialog = (staffMember = null) => {
    if (staffMember) {
      setEditingStaff(staffMember);
      setFormData({
        user_id: staffMember.user_id,
        position: staffMember.position || '',
        usual_shift: staffMember.usual_shift || 'Full Day',
        certifications: (staffMember.certifications || []).map(c => typeof c === 'object' ? c.name : c).join(', '),
        hire_date: staffMember.hire_date || '',
        training_completed: (staffMember.training_completed || []).join(', ')
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const openProfileWithHistory = async (member) => {
    setSelectedStaffProfile(member);
    setProfileDialogOpen(true);
    
    // Fetch attendance history for admin
    if (isAdmin) {
      try {
        const response = await axios.get(`${API}/timeclock/staff-attendance-history/${member.id}`, getAuthHeaders());
        setSelectedStaffProfile(prev => ({
          ...prev,
          attendance_issues: response.data.issues || [],
          attendance_summary: response.data.summary || null
        }));
      } catch (error) {
        console.log('Could not fetch attendance history:', error);
      }
    }
  };

  const handleInviteStaff = async (e) => {
    e.preventDefault();
    setInviting(true);
    setInviteResult(null);
    
    try {
      const response = await axios.post(`${API}/staff/invite`, inviteData, getAuthHeaders());
      setInviteResult({
        success: true,
        message: response.data.message,
        email_sent: response.data.email_sent,
        temp_password: response.data.temp_password,
        email: response.data.email
      });
      if (response.data.email_sent) {
        toast.success('Invite email sent! Credentials shown below in case they need to be shared manually.');
      } else {
        toast.warning('Staff created but email could not be sent. Please share the credentials below manually.');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite staff member');
      setInviteResult({
        success: false,
        message: error.response?.data?.detail || 'Failed to invite staff member'
      });
    } finally {
      setInviting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      certifications: formData.certifications.split(',').map(c => c.trim()).filter(Boolean),
      training_completed: formData.training_completed.split(',').map(t => t.trim()).filter(Boolean)
    };

    try {
      if (editingStaff) {
        // Don't send user_id on updates
        const { user_id, ...updatePayload } = payload;
        await axios.put(`${API}/staff/${editingStaff.id}`, updatePayload, getAuthHeaders());
        toast.success('Staff member updated successfully');
      } else {
        await axios.post(`${API}/staff`, payload, getAuthHeaders());
        toast.success('Staff member added successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save staff member');
    }
  };

  const handleDeleteClick = (member) => {
    setStaffToDelete(member);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!staffToDelete) return;
    
    try {
      await axios.delete(`${API}/staff/${staffToDelete.id}`, getAuthHeaders());
      toast.success('Staff member archived successfully');
      setDeleteDialogOpen(false);
      setStaffToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive staff member');
    }
  };

  const handleRestoreClick = (member) => {
    setStaffToRestore(member);
    setRestoreDialogOpen(true);
  };

  const handleConfirmRestore = async () => {
    if (!staffToRestore) return;
    
    try {
      await axios.put(`${API}/staff/${staffToRestore.id}/restore`, {}, getAuthHeaders());
      toast.success(`${staffToRestore.user_name || 'Staff member'} restored successfully`);
      setRestoreDialogOpen(false);
      setStaffToRestore(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore staff member');
    }
  };

  const handlePermanentDeleteClick = (member) => {
    setStaffToPermanentDelete(member);
    setPermanentDeleteDialogOpen(true);
  };

  const handleConfirmPermanentDelete = async () => {
    if (!staffToPermanentDelete) return;
    
    try {
      await axios.delete(`${API}/staff/${staffToPermanentDelete.id}/permanent`, getAuthHeaders());
      toast.success('Staff member permanently deleted');
      setPermanentDeleteDialogOpen(false);
      setStaffToPermanentDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to permanently delete staff member');
    }
  };

  const handleSuspendClick = (member) => {
    if (!member.user_id) {
      toast.error('Cannot suspend: This staff member has no linked user account');
      return;
    }
    setStaffToSuspend(member);
    setSuspendDialogOpen(true);
  };

  const handleConfirmSuspend = async () => {
    if (!staffToSuspend) return;
    
    setSuspendLoading(true);
    const isSuspended = staffToSuspend.is_active === false;
    const endpoint = isSuspended ? 'reactivate' : 'deactivate';
    
    try {
      await axios.put(`${API}/auth/users/${staffToSuspend.user_id}/${endpoint}`, {}, getAuthHeaders());
      toast.success(
        isSuspended 
          ? `${staffToSuspend.user_name}'s account has been reactivated`
          : `${staffToSuspend.user_name}'s account has been suspended`
      );
      setSuspendDialogOpen(false);
      setStaffToSuspend(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${isSuspended ? 'reactivate' : 'suspend'} account`);
    } finally {
      setSuspendLoading(false);
    }
  };

  const handleOpenPermissions = async (member) => {
    setSelectedStaffForPermissions(member);
    try {
      const res = await axios.get(`${API}/staff/${member.id}/permissions`, getAuthHeaders());
      setPermissions(res.data);
    } catch (error) {
      setPermissions({
        can_manage_children: true,
        can_manage_attendance: true,
        can_view_billing: false,
        can_create_invoices: false,
        can_send_messages: true,
        can_create_reports: true,
        can_view_compliance: true,
        can_edit_compliance: false
      });
    }
    setPermissionsDialogOpen(true);
  };

  const handleSavePermissions = async () => {
    if (!selectedStaffForPermissions) return;
    
    try {
      await axios.put(`${API}/staff/${selectedStaffForPermissions.id}/permissions`, permissions, getAuthHeaders());
      toast.success('Permissions updated successfully');
      setPermissionsDialogOpen(false);
    } catch (error) {
      toast.error('Failed to update permissions');
    }
  };

  // DCF Forms Functions
  const handleOpenDCFDialog = (member) => {
    setSelectedStaffForDCF(member);
    setDcfChecklist(member.dcf_checklist || {});
    setOrientationStatus(member.orientation_completed || {});
    setDcfDialogOpen(true);
    loadFormDocs(member.id);
  };

  const handleChecklistChange = (itemId, value) => {
    setDcfChecklist(prev => ({
      ...prev,
      [itemId]: value
    }));
  };

  const handleOrientationChange = (itemId, completed) => {
    setOrientationStatus(prev => ({
      ...prev,
      [itemId]: {
        completed,
        date: completed ? new Date().toISOString().split('T')[0] : '',
        trainer: ''
      }
    }));
  };

  const handleSaveDCFChecklist = async () => {
    if (!selectedStaffForDCF) return;
    
    try {
      await Promise.all([
        axios.put(`${API}/staff/${selectedStaffForDCF.id}/dcf-checklist`, dcfChecklist, getAuthHeaders()),
        axios.put(`${API}/staff/${selectedStaffForDCF.id}/orientation`, orientationStatus, getAuthHeaders())
      ]);
      toast.success('DCF records updated successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to update DCF records');
    }
  };

  const handleOrientationDocUpload = async (itemId, file) => {
    if (!file || !selectedStaffForDCF) return;
    
    setUploadingOrientation(itemId);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(
        `${API}/staff/${selectedStaffForDCF.id}/documents/orientation_${itemId}`,
        formData,
        {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success('Document uploaded successfully');
      setOrientationDocs(prev => ({
        ...prev,
        [itemId]: response.data.document
      }));
      // Auto-mark as completed when document is uploaded
      handleOrientationChange(itemId, true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadingOrientation(null);
    }
  };

  const downloadForm = async (staffId, formType, staffName) => {
    try {
      let url = `${API}/staff/${staffId}/forms/${formType}`;
      if (formType === 'dcf-1675a' && (dateRangeStart || dateRangeEnd)) {
        const params = new URLSearchParams();
        if (dateRangeStart) params.append('start_date', dateRangeStart);
        if (dateRangeEnd) params.append('end_date', dateRangeEnd);
        url += `?${params.toString()}`;
      }
      
      const response = await axios.get(url, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${formType.toUpperCase()}_${staffName.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success(`${formType.toUpperCase()} downloaded successfully`);
    } catch (error) {
      toast.error('Failed to download form');
    }
  };

  const handleStaffPhotoUpload = async (staffId, file) => {
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/staff/${staffId}/photo`, fd, {
        ...getAuthHeaders(),
        headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Photo uploaded');
      fetchData();
      if (selectedStaffProfile?.id === staffId) {
        setSelectedStaffProfile(prev => ({ ...prev, photo_url: `/api/staff/${staffId}/photo?t=${Date.now()}` }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDeleteStaffPhoto = async (staffId) => {
    try {
      await axios.delete(`${API}/staff/${staffId}/photo`, getAuthHeaders());
      toast.success('Photo removed');
      fetchData();
      if (selectedStaffProfile?.id === staffId) {
        setSelectedStaffProfile(prev => ({ ...prev, photo_url: null }));
      }
    } catch (error) {
      toast.error('Failed to remove photo');
    }
  };

  const handleSaveStaffName = async () => {
    if (!selectedStaffProfile || !editStaffName.trim()) return;
    try {
      // Update user name
      await axios.put(`${API}/staff/${selectedStaffProfile.id}`, {
        name: editStaffName.trim()
      }, getAuthHeaders());
      // Also update the user document name
      if (selectedStaffProfile.user_id) {
        await axios.put(`${API}/users/${selectedStaffProfile.user_id}/name`, {
          name: editStaffName.trim()
        }, getAuthHeaders()).catch(() => {});
      }
      toast.success('Name updated');
      setEditingStaffName(false);
      setSelectedStaffProfile(prev => ({ ...prev, user_name: editStaffName.trim() }));
      fetchData();
    } catch (error) {
      toast.error('Failed to update name');
    }
  };

  const getPositionColor = (position) => {
    const colors = {
      'Center Director': 'bg-purple-100 text-purple-800',
      'Director': 'bg-purple-100 text-purple-800',
      'Lead Teacher': 'bg-blue-100 text-blue-800',
      'Assistant Teacher': 'bg-green-100 text-green-800',
      'Assistant Child Care Teacher': 'bg-green-100 text-green-800',
      'Program Aide': 'bg-yellow-100 text-yellow-800',
      'Aide': 'bg-yellow-100 text-yellow-800',
      'Meal Preparation Personnel': 'bg-orange-100 text-orange-800',
      'Volunteer': 'bg-gray-100 text-gray-800',
      'Administrator': 'bg-indigo-100 text-indigo-800',
      'Driver': 'bg-teal-100 text-teal-800',
      'Van Driver': 'bg-teal-100 text-teal-800'
    };
    return colors[position] || 'bg-gray-100 text-gray-800';
  };

  const handleFormDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedStaffForDCF || !pendingUploadDocType) return;
    
    setUploadingFormDoc(pendingUploadDocType);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(
        `${API}/staff/${selectedStaffForDCF.id}/documents/${pendingUploadDocType}`,
        formData,
        {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success('Document uploaded successfully');
      loadFormDocs(selectedStaffForDCF.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadingFormDoc(null);
      setPendingUploadDocType(null);
      if (formFileInputRef.current) formFileInputRef.current.value = '';
    }
  };

  const triggerFormUpload = (docType) => {
    setPendingUploadDocType(docType);
    setTimeout(() => formFileInputRef.current?.click(), 0);
  };

  const loadFormDocs = async (staffId) => {
    try {
      const res = await axios.get(`${API}/staff/${staffId}/documents`, getAuthHeaders());
      setUploadedFormDocs(res.data.documents || []);
    } catch {
      setUploadedFormDocs([]);
    }
  };

  const getUploadedDoc = (docType) => {
    return uploadedFormDocs.filter(d => d.type === docType).sort((a, b) => 
      new Date(b.uploaded_at) - new Date(a.uploaded_at)
    )[0];
  };

  const getChecklistCompletionRate = (member) => {
    const checklist = member.dcf_checklist || {};
    const met = Object.values(checklist).filter(v => v === 'X').length;
    const total = dcfChecklistItems.length;
    return total > 0 ? Math.round((met / total) * 100) : 0;
  };

  if (loading) {
    return (
      <DashboardLayout title="Staff Management">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Staff Management">
      <div className="space-y-6" data-testid="staff-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <p className="text-muted-foreground">Manage staff members, DCF compliance records, and training</p>
          {isAdmin && (
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto" data-testid="staff-header-actions">
              {/* Invite New Staff Button */}
              <Button 
                variant="outline" 
                onClick={() => setInviteDialogOpen(true)}
                data-testid="invite-staff-btn"
              >
                <Mail className="w-4 h-4 mr-2" />
                Invite New Staff
              </Button>
              
              <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-staff-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Existing User
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-heading">
                      {editingStaff ? 'Edit Staff Member' : 'Add Staff Member'}
                    </DialogTitle>
                    <DialogDescription>
                      {editingStaff ? 'Update staff member information' : 'Add an existing user as staff'}
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                    {!editingStaff && (
                      <div className="space-y-2">
                        <Label htmlFor="user_id">Select User</Label>
                        <Select 
                          value={formData.user_id} 
                          onValueChange={(value) => setFormData({...formData, user_id: value})}
                        >
                          <SelectTrigger data-testid="staff-user-select">
                            <SelectValue placeholder="Select a user" />
                          </SelectTrigger>
                          <SelectContent>
                            {users.map((u) => (
                              <SelectItem key={u.id} value={u.id}>
                                {u.name} ({u.email})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                  <div className="space-y-2">
                    <Label htmlFor="position">Position</Label>
                    <Select 
                      value={formData.position} 
                      onValueChange={(value) => setFormData({...formData, position: value})}
                    >
                      <SelectTrigger data-testid="staff-position-select">
                        <SelectValue placeholder="Select position" />
                      </SelectTrigger>
                      <SelectContent>
                        {POSITIONS.map((pos) => (
                          <SelectItem key={pos.value} value={pos.value}>
                            {pos.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="usual_shift">Usual Shift</Label>
                    <Select 
                      value={formData.usual_shift} 
                      onValueChange={(value) => setFormData({...formData, usual_shift: value})}
                    >
                      <SelectTrigger data-testid="staff-shift-select">
                        <SelectValue placeholder="Select shift" />
                      </SelectTrigger>
                      <SelectContent>
                        {shiftTimes.map((s) => (
                          <SelectItem key={s.id} value={s.name}>{s.name} ({formatTime(s.start_time)}–{formatTime(s.end_time)})</SelectItem>
                        ))}
                        {shiftTimes.length === 0 && <SelectItem value="Full Day">Full Day</SelectItem>}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="hire_date">Hire Date</Label>
                    <Input
                      id="hire_date"
                      type="date"
                      value={formData.hire_date}
                      onChange={(e) => setFormData({...formData, hire_date: e.target.value})}
                      data-testid="staff-hire-date-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="certifications">Certifications (comma separated)</Label>
                    <Input
                      id="certifications"
                      value={formData.certifications}
                      onChange={(e) => setFormData({...formData, certifications: e.target.value})}
                      placeholder="e.g., CPR, First Aid, CDA"
                      data-testid="staff-certifications-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="training_completed">Training Completed (comma separated)</Label>
                    <Input
                      id="training_completed"
                      value={formData.training_completed}
                      onChange={(e) => setFormData({...formData, training_completed: e.target.value})}
                      placeholder="e.g., Child Development, Safety Training"
                      data-testid="staff-training-input"
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={!editingStaff && (!formData.user_id || !formData.position)}
                      data-testid="save-staff-btn"
                    >
                      {editingStaff ? 'Update' : 'Add'} Staff Member
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            </div>
          )}
        </div>

        {/* Search Bar & Shift Filter */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search staff..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="staff-search-input"
            />
          </div>
          <Select value={shiftFilter} onValueChange={setShiftFilter}>
            <SelectTrigger className="w-full sm:w-44" data-testid="staff-shift-filter">
              <Clock className="w-4 h-4 mr-2 text-muted-foreground" />
              <SelectValue placeholder="Filter by shift" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Shifts</SelectItem>
              {shiftTimes.map((s) => (
                <SelectItem key={s.id} value={s.name}>{s.name} ({formatTime(s.start_time)}–{formatTime(s.end_time)})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Staff Tabs - Active, Archived, and Form Compliance */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="flex flex-wrap h-auto w-full max-w-2xl mb-6 gap-1 sm:grid sm:grid-cols-3">
            <TabsTrigger value="active" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Active Staff ({staff.length})
            </TabsTrigger>
            <TabsTrigger value="archived" className="flex items-center gap-2">
              <Archive className="w-4 h-4" />
              Archived ({archivedStaff.length})
            </TabsTrigger>
            <TabsTrigger value="compliance" className="flex items-center gap-2" data-testid="tab-form-compliance">
              <ClipboardCheck className="w-4 h-4" />
              Form & Document Compliance
            </TabsTrigger>
          </TabsList>

          <TabsContent value="active">
            {/* Staff Grid - Grouped by Position */}
            {(() => {
              const filteredStaff = staff.filter(s => {
                if (shiftFilter !== 'all' && (s.usual_shift || 'Full Day') !== shiftFilter) return false;
                if (!searchQuery.trim()) return true;
                const query = searchQuery.toLowerCase();
                const name = (s.name || '').toLowerCase();
                const email = (s.email || '').toLowerCase();
                const position = (s.position || '').toLowerCase();
                return name.includes(query) || email.includes(query) || position.includes(query);
              });
              
              return filteredStaff.length > 0 ? (
              <div className="space-y-8">
                {/* Group staff by position */}
                {Object.entries(
                  filteredStaff.reduce((groups, member) => {
                    const pos = member.position || 'Unassigned';
                    if (!groups[pos]) groups[pos] = [];
                    groups[pos].push(member);
                    return groups;
                  }, {})
                )
                .sort(([a], [b]) => {
                  // Sort positions by hierarchy
                  const order = ['Center Director', 'Director', 'Administrator', 'Lead Teacher', 'Assistant Teacher', 'Assistant Child Care Teacher', 'Float Teacher', 'Program Aide', 'Van Driver', 'Meal Preparation Personnel', 'Volunteer', 'Aide', 'Unassigned'];
                  return order.indexOf(a) - order.indexOf(b);
                })
                .map(([position, members]) => (
                  <div key={position} className="space-y-4">
                    {/* Position Header */}
                    <div className="flex items-center gap-3 pb-2 border-b">
                      <Badge className={`${getPositionColor(position)} px-3 py-1`}>
                        {position}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        {members.length} {members.length === 1 ? 'employee' : 'employees'}
                      </span>
                    </div>
                    
                    {/* Staff Cards for this position */}
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {members.map((member) => (
                    <Card key={member.id} className={`card-clay overflow-hidden ${member.is_active === false ? 'opacity-75 border-red-200' : ''}`} data-testid={`staff-card-${member.id}`}>
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between gap-2 mb-3">
                          <div className="flex items-center gap-3 min-w-0">
                            {member.photo_url ? (
                              <img 
                                src={`${API}${member.photo_url}`.replace('/api/api/', '/api/')} 
                                alt={member.user_name || 'Staff'}
                                className="w-12 h-12 rounded-full object-cover shadow-sm"
                                data-testid={`staff-photo-${member.id}`}
                              />
                            ) : (
                              <div className={`w-12 h-12 rounded-full flex items-center justify-center shadow-sm ${member.is_active === false ? 'bg-gradient-to-br from-red-100 to-red-200' : 'bg-gradient-to-br from-blue-100 to-blue-200'}`}>
                                <span className={`font-bold text-lg ${member.is_active === false ? 'text-red-600' : 'text-blue-600'}`}>
                                  {member.user_name?.charAt(0).toUpperCase() || 'S'}
                                </span>
                              </div>
                            )}
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="font-semibold break-words">{member.user_name || member.name || 'Staff Member'}</h3>
                                {member.is_active === false && (
                                  <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">
                                    Suspended
                                  </Badge>
                                )}
                                {member.status === 'pending_invite' && (
                                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
                                    Pending Invite
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground truncate">{member.user_email || member.email}</p>
                            </div>
                          </div>
                          {isAdmin && (
                            <div className="flex gap-1">
                              <Button 
                                size="icon" 
                                variant="ghost" 
                                className="h-7 w-7"
                                onClick={() => handleOpenDialog(member)}
                                data-testid={`edit-staff-${member.id}`}
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                              {member.user_id && (
                                <Button 
                                  size="icon" 
                                  variant="ghost" 
                                  className={`h-7 w-7 ${member.is_active === false ? 'text-green-600 hover:text-green-700' : 'text-amber-600 hover:text-amber-700'}`}
                                  onClick={() => handleSuspendClick(member)}
                                  data-testid={`suspend-staff-${member.id}`}
                                  title={member.is_active === false ? 'Reactivate Account' : 'Suspend Account'}
                                >
                                  {member.is_active === false ? <UserCheck className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                                </Button>
                              )}
                              <Button 
                                size="icon" 
                                variant="ghost" 
                                className="h-7 w-7 text-destructive hover:text-destructive"
                                onClick={() => handleDeleteClick(member)}
                                data-testid={`delete-staff-${member.id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          )}
                        </div>

                        <div className="space-y-2 text-sm">
                          {member.user_email && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Mail className="w-4 h-4 flex-shrink-0" />
                              <span className="truncate">{member.user_email}</span>
                            </div>
                          )}
                          {member.phone && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Phone className="w-4 h-4 flex-shrink-0" />
                              <span>{member.phone}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Calendar className="w-4 h-4 flex-shrink-0" />
                            <span>Hired: {member.hire_date || 'Not specified'}</span>
                          </div>
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Clock className="w-4 h-4 flex-shrink-0" />
                            <span>Shift: {member.usual_shift || 'Not set'}
                              {(() => { const st = shiftTimes.find(s => s.name === member.usual_shift); return st ? ` (${formatTime(st.start_time)}–${formatTime(st.end_time)})` : ''; })()}
                            </span>
                          </div>
                        </div>

                  {/* DCF Compliance Progress */}
                  <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-muted-foreground">DCF Compliance</span>
                      <span className="text-xs font-bold text-primary">{getChecklistCompletionRate(member)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all ${
                          getChecklistCompletionRate(member) === 100 ? 'bg-green-500' : 
                          getChecklistCompletionRate(member) >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${getChecklistCompletionRate(member)}%` }}
                      />
                    </div>
                  </div>

                  {member.certifications && member.certifications.length > 0 && (
                    <div className="mt-4">
                      <div className="flex items-center gap-1 text-sm text-muted-foreground mb-2">
                        <Award className="w-4 h-4" />
                        <span>Certifications</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {member.certifications.slice(0, 3).map((cert, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {typeof cert === 'object' ? cert.name : cert}
                          </Badge>
                        ))}
                        {member.certifications.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{member.certifications.length - 3} more
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="mt-3 pt-3 border-t space-y-2">
                    {/* Send Invite Button for pending staff */}
                    {isAdmin && member.status === 'pending_invite' && (
                      <Button 
                        variant="default"
                        size="sm" 
                        className="w-full bg-green-600 hover:bg-green-700"
                        onClick={async () => {
                          try {
                            const res = await axios.post(`${API}/staff/${member.id}/send-invite`, {}, getAuthHeaders());
                            toast.success(res.data.message);
                            if (res.data.temp_password) {
                              toast.info(`Temp password: ${res.data.temp_password}`, { duration: 15000 });
                            }
                            fetchStaff();
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to send invite');
                          }
                        }}
                        data-testid={`send-invite-${member.id}`}
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Send Invite
                      </Button>
                    )}
                    {/* Staff Records Button */}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full"
                      onClick={() => handleOpenDCFDialog(member)}
                      data-testid={`staff-records-${member.id}`}
                    >
                      <ClipboardCheck className="w-4 h-4 mr-2" />
                      Qualifications & Records
                    </Button>
                    
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="w-full"
                      onClick={() => openProfileWithHistory(member)}
                      data-testid={`view-profile-${member.id}`}
                    >
                      <User className="w-4 h-4 mr-2" />
                      View Profile
                    </Button>
                    
                    {isAdmin && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="w-full"
                        onClick={() => handleOpenPermissions(member)}
                        data-testid={`permissions-staff-${member.id}`}
                      >
                        <Shield className="w-4 h-4 mr-2" />
                        Manage Permissions
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Card className="card-clay">
            <CardContent className="p-12 text-center">
              <UserCheck className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-heading font-bold text-xl mb-2">
                {searchQuery ? 'No Staff Found' : 'No Staff Members'}
              </h3>
              <p className="text-muted-foreground mb-6">
                {searchQuery 
                  ? `No staff match "${searchQuery}". Try a different search.`
                  : 'Add staff members to manage your team and track certifications.'}
              </p>
              {!searchQuery && isAdmin && (
                <Button onClick={() => setInviteDialogOpen(true)} data-testid="empty-add-staff-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Invite Staff Member
                </Button>
              )}
            </CardContent>
          </Card>
        );
        })()}
          </TabsContent>

          <TabsContent value="archived">
            {archivedStaff.length > 0 ? (
              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <Archive className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-amber-900">Archived Staff Members</h4>
                      <p className="text-sm text-amber-700">
                        These staff members have been archived and cannot access the platform. 
                        You can restore them to active status or permanently delete them.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {archivedStaff.map((member) => (
                    <Card key={member.id} className="card-clay opacity-75 border-amber-200" data-testid={`archived-staff-${member.id}`}>
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center shadow-sm">
                              <span className="font-bold text-lg text-gray-500">
                                {member.user_name?.charAt(0).toUpperCase() || 'S'}
                              </span>
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-gray-600">{member.user_name || 'Staff Member'}</h3>
                                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
                                  Archived
                                </Badge>
                              </div>
                              <p className="text-xs text-muted-foreground">{member.user_email}</p>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-2 text-sm mb-4">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Award className="w-4 h-4 flex-shrink-0" />
                            <span>{member.position || 'Unassigned'}</span>
                          </div>
                          {member.archived_at && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Calendar className="w-4 h-4 flex-shrink-0" />
                              <span>Archived: {new Date(member.archived_at).toLocaleDateString()}</span>
                            </div>
                          )}
                        </div>

                        {isAdmin && (
                          <div className="flex gap-2">
                            <Button 
                              variant="outline"
                              size="sm"
                              className="flex-1 text-green-600 border-green-200 hover:bg-green-50"
                              onClick={() => handleRestoreClick(member)}
                              data-testid={`restore-staff-${member.id}`}
                            >
                              <RotateCcw className="w-4 h-4 mr-2" />
                              Restore
                            </Button>
                            <Button 
                              variant="outline"
                              size="sm"
                              className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => handlePermanentDeleteClick(member)}
                              data-testid={`permanent-delete-staff-${member.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete Forever
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ) : (
              <Card className="card-clay">
                <CardContent className="p-12 text-center">
                  <Archive className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h3 className="font-heading font-bold text-xl mb-2">No Archived Staff</h3>
                  <p className="text-muted-foreground">
                    When you archive staff members, they will appear here. 
                    Archived staff can be restored or permanently deleted.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          {/* Form Compliance Tab */}
          <TabsContent value="compliance">
            <FormComplianceDashboard 
              getAuthHeaders={getAuthHeaders}
              onOpenForm={(staffMember, formType) => {
                // Set the selected staff for the form dialogs
                setSelectedStaffForDCF({
                  id: staffMember.staff_id,
                  user_name: staffMember.staff_name,
                  position: staffMember.position,
                  hire_date: staffMember.hire_date
                });
                // Open the appropriate form
                if (formType === 'staff-record') {
                  setStaffRecordFormOpen(true);
                } else if (formType === 'orientation') {
                  setOrientationChecklistFormOpen(true);
                } else if (formType === 'continuing-education') {
                  setContinuingEducationFormOpen(true);
                }
              }}
            />
          </TabsContent>
        </Tabs>

        {/* Delete/Archive Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Archive Staff Member</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to archive <strong>{staffToDelete?.user_name}</strong>? 
                They will no longer be able to access the platform, but you can restore them later from the Archived tab.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setStaffToDelete(null)}>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleConfirmDelete}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="confirm-delete-staff"
              >
                <Archive className="w-4 h-4 mr-2" />
                Archive
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Restore Confirmation Dialog */}
        <AlertDialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Restore Staff Member</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to restore <strong>{staffToRestore?.user_name}</strong>? 
                They will be able to access the platform again.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setStaffToRestore(null)}>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleConfirmRestore}
                className="bg-green-600 hover:bg-green-700"
                data-testid="confirm-restore-staff"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Restore
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Permanent Delete Confirmation Dialog */}
        <AlertDialog open={permanentDeleteDialogOpen} onOpenChange={setPermanentDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="text-red-600">Permanently Delete Staff Member</AlertDialogTitle>
              <AlertDialogDescription>
                <span className="text-red-600 font-semibold">Warning: This action cannot be undone!</span>
                <br /><br />
                Are you sure you want to permanently delete <strong>{staffToPermanentDelete?.user_name}</strong>? 
                All their data, shifts, and records will be permanently removed.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setStaffToPermanentDelete(null)}>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleConfirmPermanentDelete}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-permanent-delete-staff"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Forever
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Suspend/Reactivate Confirmation Dialog */}
        <AlertDialog open={suspendDialogOpen} onOpenChange={setSuspendDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {staffToSuspend?.is_active === false ? 'Reactivate' : 'Suspend'} Staff Account
              </AlertDialogTitle>
              <AlertDialogDescription>
                {staffToSuspend?.is_active === false ? (
                  <>
                    Are you sure you want to reactivate <strong>{staffToSuspend?.user_name}&apos;s</strong> account?
                    They will be able to log in and access the platform again.
                  </>
                ) : (
                  <>
                    Are you sure you want to suspend <strong>{staffToSuspend?.user_name}&apos;s</strong> account?
                    They will not be able to log in until their account is reactivated.
                    Their data, shifts, and records will be preserved.
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={suspendLoading} onClick={() => setStaffToSuspend(null)}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmSuspend}
                disabled={suspendLoading}
                className={staffToSuspend?.is_active === false 
                  ? "bg-green-600 hover:bg-green-700" 
                  : "bg-amber-600 hover:bg-amber-700"
                }
                data-testid="confirm-suspend-staff"
              >
                {suspendLoading 
                  ? (staffToSuspend?.is_active === false ? 'Reactivating...' : 'Suspending...') 
                  : (staffToSuspend?.is_active === false ? 'Reactivate Account' : 'Suspend Account')
                }
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Permissions Dialog */}
        <Dialog open={permissionsDialogOpen} onOpenChange={setPermissionsDialogOpen}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                Permissions for {selectedStaffForPermissions?.user_name}
              </DialogTitle>
              <DialogDescription>
                Configure what this staff member can access and do
              </DialogDescription>
            </DialogHeader>
            <ScrollArea className="h-[60vh] pr-4">
              <div className="space-y-6 mt-4">
                {/* Children Management */}
                <div>
                  <h4 className="font-semibold text-sm text-muted-foreground mb-2 flex items-center gap-2">
                    <span>👶</span> Children Management
                  </h4>
                  <div className="space-y-2">
                    {[
                      { key: 'can_add_edit_children', label: 'Add/Edit Children', desc: 'Create and modify child profiles' },
                      { key: 'can_view_children', label: 'View Only', desc: 'View child profiles without editing' },
                    ].map((perm) => (
                      <div 
                        key={perm.key} 
                        className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                          permissions[perm.key] ? 'bg-green-50' : 'bg-gray-50'
                        }`}
                      >
                        <div>
                          <p className="font-medium text-sm">{perm.label}</p>
                          <p className="text-xs text-muted-foreground">{perm.desc}</p>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={permissions[perm.key]}
                          onClick={() => setPermissions({...permissions, [perm.key]: !permissions[perm.key]})}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            permissions[perm.key] ? 'bg-primary' : 'bg-gray-300'
                          }`}
                          data-testid={`permission-toggle-${perm.key}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            permissions[perm.key] ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Waitlist */}
                <div>
                  <h4 className="font-semibold text-sm text-muted-foreground mb-2 flex items-center gap-2">
                    <span>📋</span> Waitlist
                  </h4>
                  <div className="space-y-2">
                    {[
                      { key: 'can_manage_waitlist', label: 'Manage Waitlist', desc: 'Add, edit, and manage waitlist entries' },
                    ].map((perm) => (
                      <div 
                        key={perm.key} 
                        className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                          permissions[perm.key] ? 'bg-green-50' : 'bg-gray-50'
                        }`}
                      >
                        <div>
                          <p className="font-medium text-sm">{perm.label}</p>
                          <p className="text-xs text-muted-foreground">{perm.desc}</p>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={permissions[perm.key]}
                          onClick={() => setPermissions({...permissions, [perm.key]: !permissions[perm.key]})}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            permissions[perm.key] ? 'bg-primary' : 'bg-gray-300'
                          }`}
                          data-testid={`permission-toggle-${perm.key}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            permissions[perm.key] ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Staff Profiles */}
                <div>
                  <h4 className="font-semibold text-sm text-muted-foreground mb-2 flex items-center gap-2">
                    <span>👥</span> Staff Profiles
                  </h4>
                  <div className="space-y-2">
                    {[
                      { key: 'can_view_staff_profiles', label: 'View Staff Profiles', desc: 'View other staff member profiles' },
                    ].map((perm) => (
                      <div 
                        key={perm.key} 
                        className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                          permissions[perm.key] ? 'bg-green-50' : 'bg-gray-50'
                        }`}
                      >
                        <div>
                          <p className="font-medium text-sm">{perm.label}</p>
                          <p className="text-xs text-muted-foreground">{perm.desc}</p>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={permissions[perm.key]}
                          onClick={() => setPermissions({...permissions, [perm.key]: !permissions[perm.key]})}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            permissions[perm.key] ? 'bg-primary' : 'bg-gray-300'
                          }`}
                          data-testid={`permission-toggle-${perm.key}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            permissions[perm.key] ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Schedules */}
                <div>
                  <h4 className="font-semibold text-sm text-muted-foreground mb-2 flex items-center gap-2">
                    <span>📅</span> Schedules
                  </h4>
                  <div className="space-y-2">
                    {[
                      { key: 'can_add_edit_schedules', label: 'Add/Edit Schedules', desc: 'Create and modify staff schedules' },
                      { key: 'can_view_schedules', label: 'View Only', desc: 'View schedules without editing' },
                    ].map((perm) => (
                      <div 
                        key={perm.key} 
                        className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                          permissions[perm.key] ? 'bg-green-50' : 'bg-gray-50'
                        }`}
                      >
                        <div>
                          <p className="font-medium text-sm">{perm.label}</p>
                          <p className="text-xs text-muted-foreground">{perm.desc}</p>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={permissions[perm.key]}
                          onClick={() => setPermissions({...permissions, [perm.key]: !permissions[perm.key]})}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            permissions[perm.key] ? 'bg-primary' : 'bg-gray-300'
                          }`}
                          data-testid={`permission-toggle-${perm.key}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            permissions[perm.key] ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Operations */}
                <div>
                  <h4 className="font-semibold text-sm text-muted-foreground mb-2 flex items-center gap-2">
                    <span>⚙️</span> Operations
                  </h4>
                  <div className="space-y-2">
                    {[
                      { key: 'can_use_ratio_calculator', label: 'Ratio Calculator', desc: 'Access staff-to-child ratio worksheet' },
                      { key: 'can_manage_attendance', label: 'Manage Attendance', desc: 'Check children in and out' },
                    ].map((perm) => (
                      <div 
                        key={perm.key} 
                        className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                          permissions[perm.key] ? 'bg-green-50' : 'bg-gray-50'
                        }`}
                      >
                        <div>
                          <p className="font-medium text-sm">{perm.label}</p>
                          <p className="text-xs text-muted-foreground">{perm.desc}</p>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={permissions[perm.key]}
                          onClick={() => setPermissions({...permissions, [perm.key]: !permissions[perm.key]})}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            permissions[perm.key] ? 'bg-primary' : 'bg-gray-300'
                          }`}
                          data-testid={`permission-toggle-${perm.key}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            permissions[perm.key] ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Compliance */}
                <div>
                  <h4 className="font-semibold text-sm text-muted-foreground mb-2 flex items-center gap-2">
                    <span>🛡️</span> Compliance
                  </h4>
                  <div className="space-y-2">
                    {[
                      { key: 'can_view_compliance', label: 'View Compliance', desc: 'View DCF compliance checklist' },
                      { key: 'can_edit_compliance', label: 'Edit Compliance', desc: 'Mark compliance items complete' },
                    ].map((perm) => (
                      <div 
                        key={perm.key} 
                        className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                          permissions[perm.key] ? 'bg-green-50' : 'bg-gray-50'
                        }`}
                      >
                        <div>
                          <p className="font-medium text-sm">{perm.label}</p>
                          <p className="text-xs text-muted-foreground">{perm.desc}</p>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={permissions[perm.key]}
                          onClick={() => setPermissions({...permissions, [perm.key]: !permissions[perm.key]})}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            permissions[perm.key] ? 'bg-primary' : 'bg-gray-300'
                          }`}
                          data-testid={`permission-toggle-${perm.key}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            permissions[perm.key] ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Billing */}
                <div>
                  <h4 className="font-semibold text-sm text-muted-foreground mb-2 flex items-center gap-2">
                    <span>💰</span> Billing
                  </h4>
                  <div className="space-y-2">
                    {[
                      { key: 'can_view_billing', label: 'View Billing', desc: 'View invoices and payment history' },
                      { key: 'can_create_invoices', label: 'Create Invoices', desc: 'Generate new invoices' },
                    ].map((perm) => (
                      <div 
                        key={perm.key} 
                        className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                          permissions[perm.key] ? 'bg-green-50' : 'bg-gray-50'
                        }`}
                      >
                        <div>
                          <p className="font-medium text-sm">{perm.label}</p>
                          <p className="text-xs text-muted-foreground">{perm.desc}</p>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={permissions[perm.key]}
                          onClick={() => setPermissions({...permissions, [perm.key]: !permissions[perm.key]})}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            permissions[perm.key] ? 'bg-primary' : 'bg-gray-300'
                          }`}
                          data-testid={`permission-toggle-${perm.key}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            permissions[perm.key] ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Messaging */}
                <div>
                  <h4 className="font-semibold text-sm text-muted-foreground mb-2 flex items-center gap-2">
                    <span>💬</span> Messaging
                  </h4>
                  <div className="space-y-2">
                    {[
                      { key: 'can_message_parents_and_staff', label: 'Message Parents & Staff', desc: 'Send messages to parents and other staff' },
                      { key: 'can_message_staff_only', label: 'Message Staff Only', desc: 'Send messages to administrators/directors only' },
                    ].map((perm) => (
                      <div 
                        key={perm.key} 
                        className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                          permissions[perm.key] ? 'bg-green-50' : 'bg-gray-50'
                        }`}
                      >
                        <div>
                          <p className="font-medium text-sm">{perm.label}</p>
                          <p className="text-xs text-muted-foreground">{perm.desc}</p>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={permissions[perm.key]}
                          onClick={() => setPermissions({...permissions, [perm.key]: !permissions[perm.key]})}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            permissions[perm.key] ? 'bg-primary' : 'bg-gray-300'
                          }`}
                          data-testid={`permission-toggle-${perm.key}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            permissions[perm.key] ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Reports */}
                <div>
                  <h4 className="font-semibold text-sm text-muted-foreground mb-2 flex items-center gap-2">
                    <span>📝</span> Reports
                  </h4>
                  <div className="space-y-2">
                    {[
                      { key: 'can_create_reports', label: 'Create Reports', desc: 'Write daily activity reports' },
                    ].map((perm) => (
                      <div 
                        key={perm.key} 
                        className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                          permissions[perm.key] ? 'bg-green-50' : 'bg-gray-50'
                        }`}
                      >
                        <div>
                          <p className="font-medium text-sm">{perm.label}</p>
                          <p className="text-xs text-muted-foreground">{perm.desc}</p>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={permissions[perm.key]}
                          onClick={() => setPermissions({...permissions, [perm.key]: !permissions[perm.key]})}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            permissions[perm.key] ? 'bg-primary' : 'bg-gray-300'
                          }`}
                          data-testid={`permission-toggle-${perm.key}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            permissions[perm.key] ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </ScrollArea>
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setPermissionsDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSavePermissions} data-testid="save-permissions-btn">
                Save Permissions
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* DCF Records Dialog */}
        <Dialog open={dcfDialogOpen} onOpenChange={setDcfDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Staff Records - {selectedStaffForDCF?.user_name}
              </DialogTitle>
              <DialogDescription>
                Manage qualifications, documents, DCF compliance checklist, and download official forms
              </DialogDescription>
            </DialogHeader>
            
            <Tabs defaultValue={selectedStaffForDCF?.position === 'Driver' ? 'forms' : 'qualifications'} className="mt-4">
              <TabsList className={`grid w-full ${selectedStaffForDCF?.position === 'Driver' ? 'grid-cols-2' : 'grid-cols-4'}`}>
                {selectedStaffForDCF?.position !== 'Driver' && (
                  <>
                    <TabsTrigger value="qualifications" data-testid="tab-qualifications">
                      <GraduationCap className="w-4 h-4 mr-1" />
                      Qualifications
                    </TabsTrigger>
                    <TabsTrigger value="performance" data-testid="tab-performance">
                      <Star className="w-4 h-4 mr-1" />
                      Performance
                    </TabsTrigger>
                    <TabsTrigger value="orientation">Orientation</TabsTrigger>
                  </>
                )}
                {selectedStaffForDCF?.position === 'Driver' && (
                  <TabsTrigger value="performance" data-testid="tab-performance">
                    <Star className="w-4 h-4 mr-1" />
                    Performance
                  </TabsTrigger>
                )}
                <TabsTrigger value="forms">Forms</TabsTrigger>
              </TabsList>
              
              {/* Qualifications Tab */}
              <TabsContent value="qualifications" className="mt-4">
                <ScrollArea className="h-[500px] pr-4">
                  <StaffQualifications 
                    staffId={selectedStaffForDCF?.id}
                    staffName={selectedStaffForDCF?.user_name}
                  />
                </ScrollArea>
              </TabsContent>
              
              {/* Performance & Disciplinary Tab */}
              <TabsContent value="performance" className="mt-4">
                <StaffPerformance 
                  staffId={selectedStaffForDCF?.id}
                  staffName={selectedStaffForDCF?.user_name}
                  position={selectedStaffForDCF?.position}
                />
              </TabsContent>
              
              {/* DCF-F-CFS2026 Orientation Checklist */}
              <TabsContent value="orientation" className="mt-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Staff Orientation Checklist</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      Upload the completed DCF-F-CFS2026 Staff Orientation Checklist
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {dcfOrientationItems.map((item) => {
                        const status = orientationStatus[item.id] || {};
                        const isCompleted = status.completed || false;
                        const uploadedDoc = orientationDocs[item.id];
                        
                        return (
                          <div 
                            key={item.id} 
                            className={`p-4 rounded-lg border-2 transition-colors ${
                              isCompleted ? 'bg-green-50 border-green-200' : 'bg-muted/30 border-dashed border-muted-foreground/30'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <Checkbox 
                                  checked={isCompleted}
                                  onCheckedChange={(checked) => handleOrientationChange(item.id, checked)}
                                  data-testid={`orientation-${item.id}`}
                                />
                                <div>
                                  <p className="font-medium">{item.label}</p>
                                  {isCompleted && status.date && (
                                    <p className="text-sm text-muted-foreground">
                                      Completed: {status.date}
                                    </p>
                                  )}
                                </div>
                              </div>
                              {isCompleted && (
                                <CheckCircle className="w-6 h-6 text-green-500" />
                              )}
                            </div>
                            
                            <div className="mt-4 flex items-center gap-3">
                              <input
                                type="file"
                                id={`orientation-upload-${item.id}`}
                                className="hidden"
                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                onChange={(e) => handleOrientationDocUpload(item.id, e.target.files[0])}
                              />
                              <Button
                                variant={uploadedDoc ? "outline" : "default"}
                                size="sm"
                                onClick={() => document.getElementById(`orientation-upload-${item.id}`).click()}
                                disabled={uploadingOrientation === item.id}
                                data-testid={`orientation-upload-btn-${item.id}`}
                              >
                                {uploadingOrientation === item.id ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-current" />
                                ) : (
                                  <>
                                    <Upload className="w-4 h-4 mr-2" />
                                    {uploadedDoc ? 'Replace File' : 'Upload Signed Form'}
                                  </>
                                )}
                              </Button>
                              {uploadedDoc && (
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => window.open(uploadedDoc.url, '_blank')}
                                    title="View document"
                                    data-testid={`view-doc-${item.id}`}
                                  >
                                    <Eye className="w-4 h-4 mr-1" />
                                    View
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      const printWindow = window.open(uploadedDoc.url, '_blank');
                                      printWindow.onload = () => printWindow.print();
                                    }}
                                    title="Print document"
                                    data-testid={`print-doc-${item.id}`}
                                  >
                                    <Printer className="w-4 h-4 mr-1" />
                                    Print
                                  </Button>
                                  <span className="text-sm text-green-600 flex items-center gap-1">
                                    <FileText className="w-4 h-4" />
                                    {uploadedDoc.original_filename}
                                  </span>
                                </div>
                              )}
                            </div>
                            
                            <p className="mt-3 text-xs text-muted-foreground">
                              Download the blank form from the Forms tab, complete it with the new employee, then upload the signed copy here.
                            </p>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex justify-end mt-4 pt-4 border-t">
                      <Button onClick={handleSaveDCFChecklist} data-testid="save-orientation-btn">
                        Save Orientation
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              
              {/* Download Forms */}
              <TabsContent value="forms" className="mt-4">
                <input
                  type="file"
                  ref={formFileInputRef}
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={handleFormDocUpload}
                  data-testid="form-file-input"
                />
                <div className="space-y-4">
                  {/* DCF-F-CFS0053 */}
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold">DCF-F-CFS0053 - Staff Record</h4>
                          <p className="text-sm text-muted-foreground">
                            Complete staff record form including education, certifications, and training
                          </p>
                          {getUploadedDoc('staff_record') && (
                            <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              Uploaded: {getUploadedDoc('staff_record').original_filename}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <Button 
                            variant="outline" size="sm"
                            onClick={() => triggerFormUpload('staff_record')}
                            disabled={uploadingFormDoc === 'staff_record'}
                            data-testid="upload-dcf-0053"
                          >
                            <Upload className="w-4 h-4 mr-1" />
                            {uploadingFormDoc === 'staff_record' ? 'Uploading...' : 'Upload'}
                          </Button>
                          <Button 
                            variant="outline" size="sm"
                            onClick={() => setStaffRecordFormOpen(true)}
                            data-testid="fill-dcf-0053"
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Fill Out
                          </Button>
                          <Button 
                            size="sm"
                            onClick={() => downloadForm(selectedStaffForDCF?.id, 'dcf-0053', selectedStaffForDCF?.user_name)}
                            data-testid="download-dcf-0053"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Download
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* DCF-F-CFS2026 - Only for non-Driver */}
                  {selectedStaffForDCF?.position !== 'Driver' && (
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold">DCF-F-CFS2026 - Orientation Checklist</h4>
                          <p className="text-sm text-muted-foreground">
                            Staff orientation checklist documenting completed orientation topics
                          </p>
                          {getUploadedDoc('orientation_checklist') && (
                            <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              Uploaded: {getUploadedDoc('orientation_checklist').original_filename}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <Button 
                            variant="outline" size="sm"
                            onClick={() => triggerFormUpload('orientation_checklist')}
                            disabled={uploadingFormDoc === 'orientation_checklist'}
                            data-testid="upload-dcf-2026"
                          >
                            <Upload className="w-4 h-4 mr-1" />
                            {uploadingFormDoc === 'orientation_checklist' ? 'Uploading...' : 'Upload'}
                          </Button>
                          <Button 
                            variant="outline" size="sm"
                            onClick={() => setOrientationChecklistFormOpen(true)}
                            data-testid="fill-dcf-2026"
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Fill Out
                          </Button>
                          <Button 
                            size="sm"
                            onClick={() => downloadForm(selectedStaffForDCF?.id, 'dcf-2026', selectedStaffForDCF?.user_name)}
                            data-testid="download-dcf-2026"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Download
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  )}
                  
                  {/* DCF-F-CFS0053A - Continuing Education Record - Only for non-Driver */}
                  {selectedStaffForDCF?.position !== 'Driver' && (
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold">DCF-F-CFS0053A - Continuing Education Record</h4>
                          <p className="text-sm text-muted-foreground">
                            Track staff continuing education hours and training for the year
                          </p>
                          {getUploadedDoc('continuing_education') && (
                            <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              Uploaded: {getUploadedDoc('continuing_education').original_filename}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <Button 
                            variant="outline" size="sm"
                            onClick={() => triggerFormUpload('continuing_education')}
                            disabled={uploadingFormDoc === 'continuing_education'}
                            data-testid="upload-dcf-0053a"
                          >
                            <Upload className="w-4 h-4 mr-1" />
                            {uploadingFormDoc === 'continuing_education' ? 'Uploading...' : 'Upload'}
                          </Button>
                          <Button 
                            variant="outline" size="sm"
                            onClick={() => setContinuingEducationFormOpen(true)}
                            data-testid="fill-dcf-0053a"
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Fill Out
                          </Button>
                          <Button 
                            size="sm"
                            onClick={() => downloadForm(selectedStaffForDCF?.id, 'dcf-0053a', selectedStaffForDCF?.user_name)}
                            data-testid="download-dcf-0053a"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Download
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  )}

                  {/* Transportation Training - Only show for Driver position */}
                  {(selectedStaffForDCF?.position === 'Driver' || selectedStaffForDCF?.position === 'Van Driver') && (
                    <Card className="border-l-4 border-l-teal-500">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold">DCF 251.08 - Transportation Driver Training Record</h4>
                            <p className="text-sm text-muted-foreground">
                              Required training and documentation for staff who transport children
                            </p>
                            {getUploadedDoc('transportation_training') && (
                              <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" />
                                Uploaded: {getUploadedDoc('transportation_training').original_filename}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <Button 
                              variant="outline" size="sm"
                              onClick={() => triggerFormUpload('transportation_training')}
                              disabled={uploadingFormDoc === 'transportation_training'}
                              data-testid="upload-transport-training"
                            >
                              <Upload className="w-4 h-4 mr-1" />
                              {uploadingFormDoc === 'transportation_training' ? 'Uploading...' : 'Upload'}
                            </Button>
                            <Button 
                              variant="outline" size="sm"
                              onClick={() => setTransportTrainingOpen(true)}
                              data-testid="fill-transport-training"
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Fill Out
                            </Button>
                            <Button 
                              size="sm"
                              onClick={() => downloadForm(selectedStaffForDCF?.id, 'transportation-training', selectedStaffForDCF?.user_name)}
                              data-testid="download-transport-training"
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Download
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Additional Document Uploads */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Upload className="w-4 h-4 text-primary" />
                        Additional Staff Records
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">
                        Upload transcripts, certificates, and other required documents
                      </p>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {(() => {
                        const isDriver = selectedStaffForDCF?.position === 'Driver' || selectedStaffForDCF?.position === 'Van Driver';
                        const teacherDocs = [
                          { key: 'health_report', name: 'Staff Health Report' },
                          { key: 'first_aid_certificate', name: 'First Aid Certificate' },
                          { key: 'cpr_certificate', name: 'CPR Certificate' },
                          { key: 'aht_certificate', name: 'AHT Training Certificate' },
                          { key: 'mandated_reporter_certificate', name: 'Mandated Reporter Certificate' },
                          { key: 'background_check_approval', name: 'Background Check Approval' },
                          { key: 'diploma', name: 'Diploma/GED' },
                          { key: 'transcripts', name: 'Transcripts' },
                          { key: 'wi_registry_certificate', name: 'WI Registry Certificate' },
                        ];
                        const driverDocs = [
                          { key: 'health_report', name: 'Staff Health Report' },
                          { key: 'first_aid_certificate', name: 'First Aid / CPR Certificate' },
                          { key: 'background_check_approval', name: 'Background Check Approval' },
                          { key: 'driving_record', name: 'Driving Record (Annual)' },
                          { key: 'drivers_license_copy', name: "Driver's License Copy" },
                          { key: 'driver_training_cert', name: 'Driver Training Completion Certificate' },
                          { key: 'vehicle_inspection', name: 'Vehicle Inspection Report' },
                        ];
                        const docs = [
                          ...(isDriver ? driverDocs : teacherDocs),
                          ...(!isDriver && (selectedStaffForDCF?.position === 'Driver' || selectedStaffForDCF?.position === 'Van Driver') ? [] : []),
                          { key: 'other', name: 'Other Documents' }
                        ];
                        return docs.map(doc => {
                        const uploaded = getUploadedDoc(doc.key);
                        return (
                          <div key={doc.key} className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/30 border border-transparent hover:border-muted-foreground/20 transition-colors">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              {uploaded ? (
                                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                              ) : (
                                <XCircle className="w-4 h-4 text-muted-foreground/40 flex-shrink-0" />
                              )}
                              <div className="min-w-0">
                                <p className="text-sm font-medium">{doc.name}</p>
                                {uploaded && (
                                  <p className="text-xs text-muted-foreground truncate">{uploaded.original_filename}</p>
                                )}
                              </div>
                            </div>
                            <Button
                              variant="outline" size="sm"
                              onClick={() => triggerFormUpload(doc.key)}
                              disabled={uploadingFormDoc === doc.key}
                              data-testid={`upload-${doc.key}`}
                              className="flex-shrink-0 ml-2"
                            >
                              <Upload className="w-3 h-3 mr-1" />
                              {uploadingFormDoc === doc.key ? 'Uploading...' : uploaded ? 'Replace' : 'Upload'}
                            </Button>
                          </div>
                        );
                      })})()}
                    </CardContent>
                  </Card>
                  
                  {/* DCF-F-CFS1675A-E with Date Range */}
                  <Card>
                    <CardContent className="p-4">
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-semibold">DCF-F-CFS1675A-E - Staff Record Checklist</h4>
                          <p className="text-sm text-muted-foreground">
                            Compliance checklist for licensing review with custom date range
                          </p>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="start_date">Start Date (Optional)</Label>
                            <Input
                              id="start_date"
                              type="date"
                              value={dateRangeStart}
                              onChange={(e) => setDateRangeStart(e.target.value)}
                              data-testid="date-range-start"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="end_date">End Date (Optional)</Label>
                            <Input
                              id="end_date"
                              type="date"
                              value={dateRangeEnd}
                              onChange={(e) => setDateRangeEnd(e.target.value)}
                              data-testid="date-range-end"
                            />
                          </div>
                        </div>
                        <Button 
                          onClick={() => downloadForm(selectedStaffForDCF?.id, 'dcf-1675a', selectedStaffForDCF?.user_name)}
                          className="w-full"
                          data-testid="download-dcf-1675a"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download Checklist PDF
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>

        {/* Staff Profile Dialog */}
        <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Staff Profile
              </DialogTitle>
            </DialogHeader>
            
            {selectedStaffProfile && (
              <div className="mt-4 space-y-6">
                {/* Profile Header */}
                <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                  <div className="relative group">
                    {selectedStaffProfile.photo_url ? (
                      <img 
                        src={`${API}${selectedStaffProfile.photo_url}`.replace('/api/api/', '/api/')} 
                        alt={selectedStaffProfile.user_name || 'Staff'}
                        className="w-16 h-16 rounded-full object-cover shadow-sm"
                        data-testid="staff-profile-photo"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center shadow-sm">
                        <span className="font-bold text-2xl text-blue-600">
                          {selectedStaffProfile.user_name?.charAt(0).toUpperCase() || 'S'}
                        </span>
                      </div>
                    )}
                    {isAdmin && (
                      <label className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity" data-testid="upload-staff-photo-trigger">
                        <Camera className="w-5 h-5 text-white" />
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => e.target.files[0] && handleStaffPhotoUpload(selectedStaffProfile.id, e.target.files[0])}
                          disabled={uploadingPhoto}
                        />
                      </label>
                    )}
                  </div>
                  <div className="flex-1">
                    {editingStaffName ? (
                      <div className="flex items-center gap-2">
                        <Input value={editStaffName} onChange={(e) => setEditStaffName(e.target.value)} placeholder="Full Name" className="h-8 w-48" data-testid="edit-staff-name" />
                        <Button size="sm" className="h-8" onClick={handleSaveStaffName} data-testid="save-staff-name-btn">Save</Button>
                        <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditingStaffName(false)}>Cancel</Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-lg">{selectedStaffProfile.user_name || 'Staff Member'}</h3>
                        {isAdmin && (
                          <Button size="sm" variant="ghost" className="h-6 px-1" onClick={() => { setEditingStaffName(true); setEditStaffName(selectedStaffProfile.user_name || ''); }} data-testid="edit-staff-name-btn">
                            <Edit className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    )}
                    <p className="text-muted-foreground">{selectedStaffProfile.position}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline">
                        Hired: {selectedStaffProfile.hire_date || 'Not specified'}
                      </Badge>
                      {selectedStaffProfile.photo_url && isAdmin && (
                        <Button size="sm" variant="ghost" className="text-xs text-red-500 h-6 px-2" onClick={() => handleDeleteStaffPhoto(selectedStaffProfile.id)} data-testid="delete-staff-photo-btn">
                          Remove Photo
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h4 className="font-semibold text-sm text-muted-foreground mb-3 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Contact Information
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-muted/30 rounded-lg">
                      <p className="text-xs text-muted-foreground">Email</p>
                      <p className="font-medium text-sm truncate">{selectedStaffProfile.user_email || 'Not provided'}</p>
                    </div>
                    <div className="p-3 bg-muted/30 rounded-lg">
                      <p className="text-xs text-muted-foreground">Phone</p>
                      <p className="font-medium text-sm">{selectedStaffProfile.phone || 'Not provided'}</p>
                    </div>
                    <div className="p-3 bg-muted/30 rounded-lg col-span-2">
                      <p className="text-xs text-muted-foreground">Address</p>
                      <p className="font-medium text-sm">{selectedStaffProfile.address || 'Not provided'}</p>
                    </div>
                    <div className="p-3 bg-muted/30 rounded-lg">
                      <p className="text-xs text-muted-foreground">Date of Birth</p>
                      <p className="font-medium text-sm">{selectedStaffProfile.date_of_birth || 'Not provided'}</p>
                    </div>
                  </div>
                </div>

                {/* Emergency Contact */}
                <div>
                  <h4 className="font-semibold text-sm text-muted-foreground mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Emergency Contact
                  </h4>
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-3">
                    {[
                      { key: 'emergency_contact_name', label: 'Name', placeholder: 'Contact name' },
                      { key: 'emergency_contact_phone', label: 'Phone', placeholder: 'Phone number' },
                      { key: 'emergency_contact_relationship', label: 'Relationship', placeholder: 'e.g., Spouse, Parent' },
                    ].map((field) => (
                      <div key={field.key} className="flex items-center justify-between gap-3">
                        <span className="text-sm text-muted-foreground w-24 shrink-0">{field.label}:</span>
                        {isAdmin ? (
                          <Input
                            value={selectedStaffProfile[field.key] || ''}
                            placeholder={field.placeholder}
                            className="h-8 text-sm flex-1"
                            onChange={(e) => setSelectedStaffProfile(prev => ({ ...prev, [field.key]: e.target.value }))}
                            data-testid={`staff-${field.key}`}
                          />
                        ) : (
                          <span className="text-sm font-medium">{selectedStaffProfile[field.key] || 'Not provided'}</span>
                        )}
                      </div>
                    ))}
                    {isAdmin && (
                      <div className="pt-2 flex justify-end">
                        <Button
                          size="sm"
                          onClick={async () => {
                            try {
                              await axios.put(
                                `${API}/staff/${selectedStaffProfile.id}`,
                                {
                                  emergency_contact_name: selectedStaffProfile.emergency_contact_name || '',
                                  emergency_contact_phone: selectedStaffProfile.emergency_contact_phone || '',
                                  emergency_contact_relationship: selectedStaffProfile.emergency_contact_relationship || '',
                                },
                                getAuthHeaders(),
                              );
                              toast.success('Emergency contact saved');
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Could not save emergency contact');
                            }
                          }}
                          data-testid="save-emergency-contact-btn"
                        >
                          <Save className="w-4 h-4 mr-1" /> Save emergency contact
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* DCF Compliance Dates */}
                <div>
                  <h4 className="font-semibold text-sm text-muted-foreground mb-3 flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    {selectedStaffProfile.position === 'Driver' ? 'Driver Qualifications — DCF 251.08(4)' : 'DCF Compliance Dates'}
                  </h4>
                  <div className="space-y-3">
                    {/* Common compliance fields */}
                    {[
                      { key: 'background_check_date', label: 'Background Check', code: 'DCF 251.05(2)(a)', interval: 'Every 4 years' },
                      { key: 'cpr_certification_date', label: 'CPR/First Aid Certification', code: 'DCF 251.05(4)', interval: 'Every 2 years' },
                      ...(selectedStaffProfile.position === 'Driver' ? [] : [
                        { key: 'training_completion_date', label: 'Staff Training / Continuing Ed', code: 'DCF 251.05(3)', interval: 'Annual' },
                      ]),
                    ].map((field) => (
                      <div key={field.key} className="p-3 bg-muted/30 rounded-lg border">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium">{field.label}</p>
                            <p className="text-xs text-muted-foreground">{field.code} — {field.interval}</p>
                          </div>
                          {isAdmin ? (
                            <ComplianceDateInput
                              value={selectedStaffProfile[field.key]}
                              testId={`staff-${field.key}`}
                              onSave={async (val) => {
                                try {
                                  await axios.put(`${API}/staff/${selectedStaffProfile.id}`, { [field.key]: val }, getAuthHeaders());
                                  setSelectedStaffProfile(prev => ({ ...prev, [field.key]: val }));
                                  toast.success(`${field.label} date updated`);
                                } catch { toast.error('Failed to update'); }
                              }}
                            />
                          ) : (
                            <span className="text-sm font-medium">
                              {selectedStaffProfile[field.key] ? new Date(selectedStaffProfile[field.key] + 'T12:00:00').toLocaleDateString() : 'Not set'}
                            </span>
                          )}
                        </div>
                        {selectedStaffProfile[field.key] && (
                          <p className="text-xs text-green-600 mt-1">
                            Completed: {new Date(selectedStaffProfile[field.key] + 'T12:00:00').toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    ))}

                    {/* Driver-specific qualification fields */}
                    {selectedStaffProfile.position === 'Driver' && (
                      <>
                        <div className="p-3 bg-teal-50 rounded-lg border border-teal-200">
                          <p className="text-xs font-semibold text-teal-700 mb-2">Driver License — DCF 251.08(4)(a)</p>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <Label className="text-xs">License Number</Label>
                              {isAdmin ? (
                                <Input value={selectedStaffProfile.driver_license_number || ''} placeholder="License #"
                                  className="h-7 text-xs"
                                  onBlur={async (e) => {
                                    try { await axios.put(`${API}/staff/${selectedStaffProfile.id}`, { driver_license_number: e.target.value }, getAuthHeaders());
                                      setSelectedStaffProfile(prev => ({ ...prev, driver_license_number: e.target.value }));
                                    } catch {}
                                  }}
                                  onChange={(e) => setSelectedStaffProfile(prev => ({ ...prev, driver_license_number: e.target.value }))}
                                  data-testid="driver-license-number"
                                />
                              ) : <p className="text-sm">{selectedStaffProfile.driver_license_number || 'Not set'}</p>}
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">License State</Label>
                              {isAdmin ? (
                                <Input value={selectedStaffProfile.driver_license_state || ''} placeholder="WI"
                                  className="h-7 text-xs"
                                  onBlur={async (e) => {
                                    try { await axios.put(`${API}/staff/${selectedStaffProfile.id}`, { driver_license_state: e.target.value }, getAuthHeaders());
                                      setSelectedStaffProfile(prev => ({ ...prev, driver_license_state: e.target.value }));
                                    } catch {}
                                  }}
                                  onChange={(e) => setSelectedStaffProfile(prev => ({ ...prev, driver_license_state: e.target.value }))}
                                  data-testid="driver-license-state"
                                />
                              ) : <p className="text-sm">{selectedStaffProfile.driver_license_state || 'Not set'}</p>}
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">License Expiry Date</Label>
                              {isAdmin ? (
                                <ComplianceDateInput
                                  value={selectedStaffProfile.driver_license_expiry}
                                  className="h-7 text-xs"
                                  testId="driver-license-expiry"
                                  onSave={async (val) => {
                                    try {
                                      await axios.put(`${API}/staff/${selectedStaffProfile.id}`, { driver_license_expiry: val }, getAuthHeaders());
                                      setSelectedStaffProfile(prev => ({ ...prev, driver_license_expiry: val }));
                                      toast.success('License expiry updated');
                                    } catch { toast.error('Failed to update'); }
                                  }}
                                />
                              ) : <p className="text-sm">{selectedStaffProfile.driver_license_expiry ? new Date(selectedStaffProfile.driver_license_expiry + 'T12:00:00').toLocaleDateString() : 'Not set'}</p>}
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Age Verified (18+)</Label>
                              <Badge className={selectedStaffProfile.driver_age_verified ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                                {selectedStaffProfile.driver_age_verified ? 'Verified' : 'Not Verified'}
                              </Badge>
                              {isAdmin && !selectedStaffProfile.driver_age_verified && (
                                <Button size="sm" variant="outline" className="h-6 text-xs ml-2" onClick={async () => {
                                  try { await axios.put(`${API}/staff/${selectedStaffProfile.id}`, { driver_age_verified: true }, getAuthHeaders());
                                    setSelectedStaffProfile(prev => ({ ...prev, driver_age_verified: true }));
                                    toast.success('Age verified');
                                  } catch {}
                                }} data-testid="verify-driver-age">Verify</Button>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="p-3 bg-teal-50 rounded-lg border border-teal-200">
                          <p className="text-xs font-semibold text-teal-700 mb-2">Driving Record — DCF 251.08(4)(c)</p>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <Label className="text-xs">Last Driving Record Date</Label>
                              {isAdmin ? (
                                <ComplianceDateInput
                                  value={selectedStaffProfile.driving_record_date}
                                  className="h-7 text-xs"
                                  testId="driving-record-date"
                                  onSave={async (val) => {
                                    try {
                                      await axios.put(`${API}/staff/${selectedStaffProfile.id}`, { driving_record_date: val }, getAuthHeaders());
                                      setSelectedStaffProfile(prev => ({ ...prev, driving_record_date: val }));
                                      toast.success('Driving record date updated');
                                    } catch { toast.error('Failed to update'); }
                                  }}
                                />
                              ) : <p className="text-sm">{selectedStaffProfile.driving_record_date ? new Date(selectedStaffProfile.driving_record_date + 'T12:00:00').toLocaleDateString() : 'Not set'}</p>}
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Record Reviewed & Clear</Label>
                              <Badge className={selectedStaffProfile.driving_record_clear ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                                {selectedStaffProfile.driving_record_clear ? 'Clear' : 'Pending Review'}
                              </Badge>
                              {isAdmin && !selectedStaffProfile.driving_record_clear && (
                                <Button size="sm" variant="outline" className="h-6 text-xs ml-2" onClick={async () => {
                                  try { await axios.put(`${API}/staff/${selectedStaffProfile.id}`, { driving_record_clear: true }, getAuthHeaders());
                                    setSelectedStaffProfile(prev => ({ ...prev, driving_record_clear: true }));
                                    toast.success('Driving record marked clear');
                                  } catch {}
                                }} data-testid="mark-record-clear">Mark Clear</Button>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="p-3 bg-teal-50 rounded-lg border border-teal-200">
                          <p className="text-xs font-semibold text-teal-700 mb-2">Driver Training — DCF 251.08(4)(b)</p>
                          {/* Training Completion Status */}
                          <div className={`p-2 rounded-lg mb-2 ${selectedStaffProfile.driver_training_complete ? 'bg-green-100 border border-green-300' : 'bg-red-100 border border-red-300'}`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                {selectedStaffProfile.driver_training_complete ? (
                                  <CheckCircle className="w-4 h-4 text-green-600" />
                                ) : (
                                  <AlertTriangle className="w-4 h-4 text-red-600" />
                                )}
                                <span className={`text-sm font-semibold ${selectedStaffProfile.driver_training_complete ? 'text-green-700' : 'text-red-700'}`}>
                                  {selectedStaffProfile.driver_training_complete ? 'CLEARED TO DRIVE' : 'NOT CLEARED — Training Incomplete'}
                                </span>
                              </div>
                              <Badge className={selectedStaffProfile.driver_training_complete ? 'bg-green-600' : 'bg-red-600'}>
                                {selectedStaffProfile.driver_training_progress || '0/14'}
                              </Badge>
                            </div>
                            {!selectedStaffProfile.driver_training_complete && (
                              <p className="text-xs text-red-600 mt-1">All 14 training checklist items must be completed before this driver can transport children. Go to Qualifications &amp; Records → Forms → Transportation Training to complete.</p>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <Label className="text-xs">Training Completion Date</Label>
                              {isAdmin ? (
                                <ComplianceDateInput
                                  value={selectedStaffProfile.driver_training_date}
                                  className="h-7 text-xs"
                                  testId="driver-training-date"
                                  onSave={async (val) => {
                                    try {
                                      await axios.put(`${API}/staff/${selectedStaffProfile.id}`, { driver_training_date: val }, getAuthHeaders());
                                      setSelectedStaffProfile(prev => ({ ...prev, driver_training_date: val }));
                                      toast.success('Driver training date updated');
                                    } catch { toast.error('Failed to update'); }
                                  }}
                                />
                              ) : <p className="text-sm">{selectedStaffProfile.driver_training_date ? new Date(selectedStaffProfile.driver_training_date + 'T12:00:00').toLocaleDateString() : 'Not set'}</p>}
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Cell Phone Policy Acknowledged</Label>
                              <Badge className={selectedStaffProfile.cellphone_policy_ack ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                                {selectedStaffProfile.cellphone_policy_ack ? 'Acknowledged' : 'Pending'}
                              </Badge>
                              {isAdmin && !selectedStaffProfile.cellphone_policy_ack && (
                                <Button size="sm" variant="outline" className="h-6 text-xs ml-2" onClick={async () => {
                                  try { await axios.put(`${API}/staff/${selectedStaffProfile.id}`, { cellphone_policy_ack: true }, getAuthHeaders());
                                    setSelectedStaffProfile(prev => ({ ...prev, cellphone_policy_ack: true }));
                                    toast.success('Cell phone policy acknowledged');
                                  } catch {}
                                }} data-testid="ack-cellphone-policy">Acknowledge</Button>
                              )}
                            </div>
                          </div>
                          <p className="text-[10px] text-teal-600 mt-2">
                            DCF 251.08(4)(d): Drivers may not use cell phones while loading, unloading, or transporting children, except for 911, emergency responders, or center emergencies when vehicle is stopped.
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Certifications - only for non-Driver */}
                {selectedStaffProfile.position !== 'Driver' && selectedStaffProfile.certifications && selectedStaffProfile.certifications.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-sm text-muted-foreground mb-3 flex items-center gap-2">
                      <Award className="w-4 h-4" />
                      Certifications
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedStaffProfile.certifications.map((cert, idx) => (
                        <Badge key={idx} variant="secondary">
                          {typeof cert === 'object' ? cert.name : cert}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Document Uploads */}
                <DocumentUploader entityType="staff" entityId={selectedStaffProfile.id} entityName={selectedStaffProfile.user_name} />

                {/* Attendance History */}
                {isAdmin && selectedStaffProfile?.attendance_issues && selectedStaffProfile.attendance_issues.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-sm text-muted-foreground mb-3 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Attendance Issues ({selectedStaffProfile.attendance_issues.length})
                    </h4>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {selectedStaffProfile.attendance_issues.slice(0, 5).map((issue, idx) => (
                        <div key={idx} className={`p-3 rounded-lg border text-sm ${
                          issue.type === 'no_show' ? 'bg-red-50 border-red-200' :
                          issue.type === 'late_arrival' ? 'bg-amber-50 border-amber-200' :
                          'bg-gray-50 border-gray-200'
                        }`}>
                          <div className="flex justify-between items-center">
                            <span className="font-medium">
                              {issue.type === 'no_show' ? 'No Show' :
                               issue.type === 'late_arrival' ? `Late (${issue.minutes_late} min)` :
                               issue.type === 'early_departure' ? `Left Early (${issue.minutes_early} min)` :
                               issue.type}
                            </span>
                            <span className="text-xs text-muted-foreground">{issue.date}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                {isAdmin && (
                  <div className="flex gap-2 pt-4 border-t">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => {
                        setProfileDialogOpen(false);
                        handleOpenDialog(selectedStaffProfile);
                      }}
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Edit Profile
                    </Button>
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => {
                        setProfileDialogOpen(false);
                        handleOpenDCFDialog(selectedStaffProfile);
                      }}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      DCF Records
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Invite New Staff Dialog */}
        <Dialog open={inviteDialogOpen} onOpenChange={(open) => { 
          setInviteDialogOpen(open); 
          if (!open) {
            setInviteData({ name: '', email: '', position: '', phone: '', hire_date: '' });
            setInviteResult(null);
          }
        }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" />
                Invite New Staff Member
              </DialogTitle>
              <DialogDescription>
                Create a new account and send login credentials via email
              </DialogDescription>
            </DialogHeader>
            
            {inviteResult?.success && !inviteResult?.email_sent ? (
              // Show temp password if email failed
              <div className="space-y-4 mt-4">
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Email could not be sent</p>
                      <p className="text-sm text-amber-700 mt-1">
                        Please share these credentials with the staff member manually:
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-4 bg-muted rounded-lg space-y-2">
                  <p><strong>Email:</strong> {inviteData.email}</p>
                  <p><strong>Temporary Password:</strong></p>
                  <p className="font-mono text-lg bg-primary text-white px-4 py-2 rounded text-center">
                    {inviteResult.temp_password}
                  </p>
                </div>
                <p className="text-sm text-muted-foreground">
                  The staff member will be asked to change their password and set a 5-digit PIN on first login.
                </p>
                <Button 
                  className="w-full" 
                  onClick={() => {
                    setInviteDialogOpen(false);
                    setInviteData({ name: '', email: '', position: '', phone: '', hire_date: '' });
                    setInviteResult(null);
                  }}
                >
                  Done
                </Button>
              </div>
            ) : inviteResult?.success && inviteResult?.email_sent ? (
              // Success message when email was sent - also show credentials as backup
              <div className="space-y-4 mt-4">
                <div className="text-center py-4">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <p className="font-semibold text-lg mt-3">Invitation Sent!</p>
                  <p className="text-muted-foreground mt-1">
                    {inviteData.name} should receive an email with login instructions.
                  </p>
                </div>
                <div className="p-4 bg-muted rounded-lg space-y-2">
                  <p className="text-sm font-medium text-muted-foreground">Login credentials (in case the email doesn't arrive):</p>
                  <p><strong>Email:</strong> {inviteResult.email || inviteData.email}</p>
                  <p><strong>Temporary Password:</strong></p>
                  <p className="font-mono text-lg bg-primary text-white px-4 py-2 rounded text-center select-all">
                    {inviteResult.temp_password}
                  </p>
                </div>
                <p className="text-xs text-muted-foreground">
                  They will be asked to change their password and set a 5-digit PIN on first login. Ask them to check spam/junk if they don't see the email.
                </p>
                <Button 
                  className="w-full" 
                  onClick={() => {
                    setInviteDialogOpen(false);
                    setInviteData({ name: '', email: '', position: '', phone: '', hire_date: '' });
                    setInviteResult(null);
                  }}
                >
                  Done
                </Button>
              </div>
            ) : (
              // Invite form
              <form onSubmit={handleInviteStaff} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="invite_name">Full Name</Label>
                  <Input
                    id="invite_name"
                    value={inviteData.name}
                    onChange={(e) => setInviteData({...inviteData, name: e.target.value})}
                    placeholder="John Smith"
                    required
                    data-testid="invite-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="invite_email">Email Address</Label>
                  <Input
                    id="invite_email"
                    type="email"
                    value={inviteData.email}
                    onChange={(e) => setInviteData({...inviteData, email: e.target.value})}
                    placeholder="john@example.com"
                    required
                    data-testid="invite-email-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="invite_phone">Phone Number</Label>
                  <Input
                    id="invite_phone"
                    type="tel"
                    value={inviteData.phone}
                    onChange={(e) => setInviteData({...inviteData, phone: e.target.value})}
                    placeholder="(555) 123-4567"
                    data-testid="invite-phone-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="invite_position">Position</Label>
                  <Select 
                    value={inviteData.position} 
                    onValueChange={(value) => setInviteData({...inviteData, position: value})}
                  >
                    <SelectTrigger data-testid="invite-position-select">
                      <SelectValue placeholder="Select position" />
                    </SelectTrigger>
                    <SelectContent>
                      {POSITIONS.map((pos) => (
                        <SelectItem key={pos.value} value={pos.value}>{pos.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="invite_hire_date">Hire Date</Label>
                  <Input
                    id="invite_hire_date"
                    type="date"
                    value={inviteData.hire_date}
                    onChange={(e) => setInviteData({...inviteData, hire_date: e.target.value})}
                    data-testid="invite-hire-date-input"
                  />
                </div>
                
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                  <p className="text-blue-800">
                    <strong>What happens next:</strong>
                  </p>
                  <ul className="text-blue-700 mt-1 ml-4 list-disc">
                    <li>Staff receives email with temporary password</li>
                    <li>On first login, they set a new password</li>
                    <li>They also set a 5-digit PIN for time clock</li>
                  </ul>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setInviteDialogOpen(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1" 
                    disabled={inviting || !inviteData.name || !inviteData.email || !inviteData.position}
                    data-testid="send-invite-btn"
                  >
                    {inviting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4 mr-2" />
                        Send Invitation
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Continuing Education Form */}
        <ContinuingEducationForm
          staffId={selectedStaffForDCF?.id}
          staffName={selectedStaffForDCF?.user_name}
          staffPosition={selectedStaffForDCF?.position}
          hireDate={selectedStaffForDCF?.hire_date}
          isOpen={continuingEducationFormOpen}
          onClose={() => setContinuingEducationFormOpen(false)}
          getAuthHeaders={getAuthHeaders}
        />
        
        {/* Staff Record Form (DCF-F-CFS0053) */}
        <StaffRecordForm
          open={staffRecordFormOpen}
          onClose={() => setStaffRecordFormOpen(false)}
          staff={selectedStaffForDCF}
          staffName={selectedStaffForDCF?.user_name}
          token={getAuthHeaders()?.headers?.Authorization?.replace('Bearer ', '')}
        />
        
        {/* Orientation Checklist Form (DCF-F-CFS2026) */}
        <OrientationChecklistForm
          open={orientationChecklistFormOpen}
          onClose={() => setOrientationChecklistFormOpen(false)}
          staff={selectedStaffForDCF}
          staffName={selectedStaffForDCF?.user_name}
          token={getAuthHeaders()?.headers?.Authorization?.replace('Bearer ', '')}
        />

        {/* Transportation Training Form (DCF 251.08) */}
        <TransportationTrainingForm
          open={transportTrainingOpen}
          onClose={() => setTransportTrainingOpen(false)}
          staff={selectedStaffForDCF}
          getAuthHeaders={getAuthHeaders}
        />
      </div>
    </DashboardLayout>
  );
};

export default StaffPage;
